import { useQuery } from '@tanstack/react-query';
import { Image, View, type ImageProps } from 'tamagui';

import { Text } from '@/components/text';
import { LoadingIndicator } from '@/components/loading-indicator';
import { queryKeys } from '@/lib/query-keys';
import { auth } from '@/lib/auth';
import { borderWidth } from '@/theme/tokens.generated';

const API_URL = process.env.EXPO_PUBLIC_API_URL;

// GET /photos/{photoId} requires a bearer token (issue #135's reveal
// gate -- see backend/internal/http/photo.go's GetPhotoHandler): a
// plain <Image source={{uri}}> can't attach an Authorization header to
// its own network request, so every photo this app renders has to go
// through this fetch-then-display path instead. profile-view's photo
// list rendered plain URIs before this existed -- a latent 401 that
// never surfaced only because no upload path existed yet to populate
// photos with (issue #136's own review).
//
// Data URI, not a blob object URL: RN's Image has no equivalent of the
// DOM's URL.createObjectURL on native, but FileReader.readAsDataURL
// works identically on native and web, so one code path covers both.
async function fetchPhotoAsDataURI(path: string): Promise<string> {
  const session = await auth.getSession();
  const headers = new Headers();

  if (session) {
    headers.set('Authorization', `Bearer ${session.accessToken}`);
  }

  const res = await fetch(`${API_URL}${path}`, { headers });

  if (!res.ok) {
    throw new Error('could not load photo');
  }

  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('could not read photo'));
    reader.readAsDataURL(blob);
  });
}

// Renders one reveal-gated photo by its API path (e.g. "/photos/abc").
// Cached by react-query on that path (staleTime: Infinity -- a photo's
// bytes never change under a stable id, only get replaced by deleting
// and re-uploading a new one with a new id), so the same photo
// rendered twice in one session fetches once.
//
// A failed load (owner deleted the account mid-session, network drop)
// renders a quiet bordered placeholder rather than an error banner --
// one missing photo in a grid of several isn't the sort of failure
// that should interrupt the screen it's on (contrast DeleteMe/
// UploadPhoto's own top-level error copy, which IS the primary thing
// that screen is doing).
//
// Props mirror the subset of Tamagui's own ImageProps profile-view
// already used for the plain-URI version this replaces (width,
// aspectRatio, objectFit, borderRadius) so call sites don't change
// shape, just the source of truth for what "uri" means.
export function AuthenticatedImage({
  path,
  accessibilityLabel,
  width,
  aspectRatio,
  objectFit,
  borderRadius,
}: {
  path: string;
  accessibilityLabel: string;
  width: ImageProps['width'];
  aspectRatio?: ImageProps['aspectRatio'];
  objectFit?: ImageProps['objectFit'];
  borderRadius?: ImageProps['borderRadius'];
}) {
  const photo = useQuery({
    queryKey: queryKeys.photo(path),
    queryFn: () => fetchPhotoAsDataURI(path),
    staleTime: Infinity,
  });

  if (photo.isPending) {
    return (
      <View
        width={width}
        aspectRatio={aspectRatio}
        borderRadius={borderRadius}
        backgroundColor="$card"
        alignItems="center"
        justifyContent="center"
      >
        <LoadingIndicator />
      </View>
    );
  }

  if (photo.isError) {
    // Issue #213: this previously rendered as a plain, unlabeled empty
    // box -- visually indistinguishable from a broken/empty state with
    // no signal that a real load failure happened (confirmed via code
    // read: this branch is exactly what the founder's screenshot
    // showed -- a real photo.isError, not a rendering artifact). A
    // quiet placeholder is still right here (one failed photo in a
    // list isn't worth an alarming full-screen error, matching this
    // component's own doc comment above), but it needs to actually say
    // something rather than look accidentally empty.
    return (
      <View
        width={width}
        aspectRatio={aspectRatio}
        borderRadius={borderRadius}
        backgroundColor="$card"
        borderWidth={borderWidth.hairline}
        borderColor="$borderColor"
        alignItems="center"
        justifyContent="center"
        p="$2"
      >
        <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" textAlign="center">
          Couldn&apos;t load photo
        </Text>
      </View>
    );
  }

  return (
    // src, not source={{uri}} -- tamagui's own Image (re-exported from
    // @tamagui/image) renders a bare `styled(View, {render: 'img'})` on
    // web with no React-Native-style source translation, unlike
    // @tamagui/image's separate createImage() helper (unused here).
    // source={{uri}} was passed straight through as a literal, invalid
    // `source="[object Object]"` HTML attribute, leaving the real `src`
    // attribute empty -- confirmed live: the photo fetch succeeded (no
    // console error) but rendered as an invisible zero-content <img>,
    // exactly matching the founder's report of an upload with no
    // visible thumbnail. Native platforms still take `source` via their
    // own Image.native.js -- this prop is web-only in effect since
    // fetchPhotoAsDataURI's data URI format works on both platforms via
    // src (RN Image also treats a `src` string as its `source.uri`).
    <Image
      src={photo.data}
      accessibilityLabel={accessibilityLabel}
      width={width}
      aspectRatio={aspectRatio}
      objectFit={objectFit}
      borderRadius={borderRadius}
    />
  );
}
